import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Chip, Drawer, IconButton, Stack, TextField, Typography,
} from '@mui/material';

import Iconify from '../Iconify';
import Scrollbar from '../Scrollbar';
import useAuth from '../../hooks/useAuth';
import useDex from '../../pages/admin/dex/hooks/UseDex';
import ChatMessage from '../../pages/admin/dex/ChatMessage';
import { useCoach } from '../../onboarding/engine/CoachProvider';

// ---------------------------------------------------------------------------
// Ask AI. Dex in coaching mode: it explains the product, says where things live,
// and offers to walk you through them.
//
// Coaching is a separate conversation mode, not a separate chatbot. It runs the
// same streaming endpoint with a different tool set: no tenant data, no writes,
// no credits. That is enforced on the server, so this component asking for
// mode 'coach' is a request, not the guarantee.
//
// Opened from the Help menu in the header and nowhere else. It used to own a
// floating button on every admin screen, which is one more thing permanently
// covering the corner of a page somebody is trying to work in. Help already has
// a home in the header, so this lives behind it.
//
// Opening it while a guide is running pauses the guide rather than fighting it
// for the viewport, and the step is still waiting when the drawer closes.
// ---------------------------------------------------------------------------

const WIDTH = 420;

const STARTERS = [
  'How do I add a company?',
  'Where do I set working hours?',
  'How is occupancy counted?',
  'Show me around DeskOS',
];

function DexMark({ size = 28 }) {
  return (
    <Box
      sx={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Iconify icon="mdi:robot-happy-outline" sx={{ color: 'white', fontSize: size * 0.58 }} />
    </Box>
  );
}

DexMark.propTypes = { size: PropTypes.number };

export default function CoachDrawer({ open, onClose }) {
  const { user } = useAuth();
  const coach = useCoach();
  const { createConversation, streamMessage } = useDex();

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // A guide taking over the screen is the answer to whatever was being asked,
  // so the drawer gets out of the way the moment one STARTS. Deliberately keyed
  // on the transition and not on `coach.running` being true: the pause below is
  // a state update, so on the render where the drawer opens over a guide that
  // is already running this would still read paused === false and slam the
  // drawer shut before anyone could type.
  const wasRunning = useRef(coach.running);
  useEffect(() => {
    if (coach.running && !wasRunning.current) onClose();
    wasRunning.current = coach.running;
  }, [coach.running, onClose]);

  // Opening while a guide is mid-step: pause rather than stop, because the
  // drawer and the spotlight both want the whole screen and a paused guide is
  // still exactly where it was.
  useEffect(() => {
    if (open && coach.running && !coach.paused) coach.pause?.();
    // Only on the transition into open. Re-running this as the guide's own
    // state changes would re-pause a guide the user just resumed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleSend = useCallback(async (text) => {
    const question = (text || input).trim();
    if (!question || streaming) return;

    let convId = conversationId;
    if (!convId) {
      const res = await createConversation({ mode: 'coach' });
      if (!res.success) {
        setMessages((prev) => [...prev, {
          id: `err-${Date.now()}`, role: 'assistant', ui_blocks: [],
          content: 'I could not start a session just now. Try again in a moment.',
        }]);
        return;
      }
      convId = res.data.id;
      setConversationId(convId);
    }

    const stamp = Date.now();
    setMessages((prev) => [
      ...prev,
      { id: `u-${stamp}`, role: 'user', content: question, ui_blocks: [] },
      { id: `a-${stamp}`, role: 'assistant', content: '', ui_blocks: [], isStreaming: true },
    ]);
    setInput('');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    const replyId = `a-${stamp}`;

    try {
      let buffer = '';
      await streamMessage(convId, question, (event) => {
        if (event.type === 'token') {
          buffer += event.content;
          const snapshot = buffer;
          setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, content: snapshot } : m)));
        } else if (event.type === 'ui_blocks') {
          setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, ui_blocks: event.blocks || [] } : m)));
        } else if (event.type === 'done' || event.type === 'cancelled') {
          setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, isStreaming: false } : m)));
        } else if (event.type === 'error') {
          setMessages((prev) => prev.map((m) => (
            m.id === replyId ? { ...m, content: `Something went wrong: ${event.message}`, isStreaming: false } : m
          )));
        }
      }, controller.signal);
    } finally {
      abortRef.current = null;
      setStreaming(false);
    }
  }, [input, streaming, conversationId, createConversation, streamMessage]);

  // Org staff only. Members have their own help, and the coach endpoint would
  // refuse them anyway.
  if (!user || user.role !== 'org') return null;

  const empty = messages.length === 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: WIDTH }, display: 'flex', flexDirection: 'column' } }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2.5, py: 2 }}>
        <DexMark />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>Ask AI</Typography>
          <Typography variant="caption" color="text.secondary">
            Ask anything about DeskOS. Free, and it never reads your data.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Stack>

        <Scrollbar sx={{ flex: 1, px: 2, py: 1 }}>
          {empty ? (
            <Box sx={{ px: 1, py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                Ask how something works and I will explain it. If there is a walkthrough for it,
                I will offer to take you through it on your own screen, step by step.
              </Typography>
              <Stack spacing={1} alignItems="flex-start">
                {STARTERS.map((q) => (
                  <Chip
                    key={q}
                    label={q}
                    variant="outlined"
                    onClick={() => handleSend(q)}
                    sx={{ cursor: 'pointer', height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            messages.map((m) => <ChatMessage key={m.id} message={m} />)
          )}
          <div ref={endRef} />
        </Scrollbar>

        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
            placeholder="How do I ...?"
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={() => handleSend()}
                  disabled={!input.trim() || streaming}
                  aria-label="Send"
                >
                  <Iconify icon={streaming ? 'mdi:dots-horizontal' : 'mdi:send'} sx={{ fontSize: 18 }} />
                </IconButton>
              ),
            }}
          />
          {coach.running && coach.paused && (
            <Button
              fullWidth
              size="small"
              variant="text"
              startIcon={<Iconify icon="mdi:play" />}
              onClick={() => { onClose(); coach.resume(); }}
              sx={{ mt: 1 }}
            >
              Back to the guide
            </Button>
          )}
        </Box>
    </Drawer>
  );
}

CoachDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
